<script>
import baseMixin from '@/mixins/baseMixin'
import { isNonEmptyArray } from '@/helpers/array-helpers'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'

export default {
  name: 'ClientGroupOverviewSchoolMultiSelector',
  mixins: [baseMixin],
  props: {
    schools: {
      type: Array,
      default: () => [],
    },
    selectedSchools: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  computed: {
    hasSchools() {
      return isNonEmptyArray(this.schools)
    },
    hasSingleSchool() {
      return this.hasSchools && this.schools.length === 1
    },
    hasSelectedSchools() {
      return isNonEmptyArray(this.selectedSchools)
    },
    hasAllSelected() {
      return this.selectedSchools.length === this.schools.length
    },
    hasSomeSelected() {
      return this.selectedSchools.length > 0 && !this.hasAllSelected
    },
    hasNoneSelected() {
      return this.selectedSchools.length === 0
    },
    compSelectAllIcon() {
      if (this.hasAllSelected) return 'mdi-checkbox-marked'
      if (this.hasSomeSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    compSingleSchoolSelectionText() {
      return this.$t(
        'clientGroupOverview.schoolSelector.allSchoolsSelectedText'
      )
    },
    compIconColour() {
      return this.selectedSchools.length > 0 ? VuetifyColourClass.primary : ''
    },
  },
  methods: {
    selectAll() {
      if (this.hasAllSelected) {
        this.updateSelection([])
      } else {
        this.updateSelection(
          this.schools.map((listItem) => listItem.locationId)
        )
      }
    },
    updateSelection(schools) {
      this.$emit('updated', schools)
    },
    determineSelectedText(firstItemSelected) {
      return this.hasAllSelected
        ? this.$t('clientGroupOverview.schoolSelector.allSchoolsSelectedText')
        : firstItemSelected
    },
  },
}
</script>

<template>
  <v-select
    v-if="!hasSingleSchool"
    data-testid="client-group-overview-school-multi-selector"
    item-text="displayName"
    item-value="locationId"
    :value="selectedSchools"
    :loading="loading"
    :disabled="loading || disabled"
    multiple
    hide-details
    :placeholder="$t('clientGroupOverview.schoolSelector.placeholderText')"
    outlined
    class="mt-0 pt-0"
    :style="{ 'max-width': isMediumOrHigher ? '450px' : '100%' }"
    :items="schools"
    @change="updateSelection"
  >
    <template v-slot:prepend-item>
      <v-list-item
        data-testid="client-group-overview-school-multi-selector-select-all-btn"
        ripple
        @mousedown.prevent
        @click="selectAll"
      >
        <v-list-item-action>
          <v-icon :color="compIconColour">
            {{ compSelectAllIcon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('clientGroupOverview.schoolSelector.selectAllOptionText') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider class="mt-2"></v-divider>
    </template>

    <template v-slot:selection="{ item, index }">
      <h6 class="text-subtitle-1">
        <span v-if="index === 0">
          {{ determineSelectedText(item.locationName) | truncateText(30) }}
        </span>
        <span
          v-if="index === 1 && !hasAllSelected"
          class="grey--text text-body-2 ml-2"
        >
          (+{{ selectedSchools.length - 1 }} others)
        </span>
      </h6>
    </template>
  </v-select>
  <v-text-field
    v-else
    data-testid="client-group-overview-school-multi-selector-single-school"
    class="mt-0 pt-0"
    :style="{ 'max-width': isMediumOrHigher ? '450px' : '100%' }"
    :value="compSingleSchoolSelectionText"
    outlined
    readonly
  ></v-text-field>
</template>
